import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { ArrowDown, ArrowUp, Plus, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { AppDialog } from "@/components/common/AppDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/lib/api/api";
import { useReplaceRecipeStages } from "@/hooks/recipes";
import { ingredientLineName, type RecipeIngredient } from "@/types/recipe";
import type { RecipeStage, RecipeStageInput } from "@/lib/api/requests/recipes";
import type { NumberedStep } from "@/lib/recipes/staged-method";
import {
  defaultHeldTarget,
  flowSummary,
  laterStages,
  normalizeRouting,
  routingFromStages,
  routingInputs,
  unassignedLines,
  type RoutedDraft,
} from "@/lib/recipes/stage-routing";

interface RecipeStagesEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipeId: number;
  stages: RecipeStage[];
  lines: RecipeIngredient[];
  steps: NumberedStep[];
}

/** A stage being edited; its route is kept by the target's key, not by a position that moves. */
interface DraftStage extends Omit<RecipeStageInput, "includeInNext" | "flowsInto">, RoutedDraft {}

let nextKey = 1;

function draftsFrom(stages: RecipeStage[]): DraftStage[] {
  const ordered = [...stages].sort((a, b) => a.sortOrder - b.sortOrder);
  const keys = ordered.map(() => nextKey++);
  const routing = routingFromStages(ordered, keys);
  return ordered.map((stage, index) => ({
    key: keys[index]!,
    kind: stage.kind,
    action: stage.action,
    detail: stage.detail ?? "",
    stepNumbers: stage.stepNumbers,
    ingredientIds: stage.ingredientIds,
    ...routing[index]!,
  }));
}

/**
 * Splits a recipe's method into stages for the card views: what each stage
 * does, which steps it covers, and which ingredients go in at it. Tasks are
 * the mise en place before the stages ("Preheat oven"), with no ingredients.
 * The list is saved as a whole.
 */
export function RecipeStagesEditorDialog({ open, onOpenChange, recipeId, stages, lines, steps }: RecipeStagesEditorDialogProps) {
  const { t } = useLingui();
  const [drafts, setDrafts] = useState<DraftStage[]>(() => draftsFrom(stages));
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDrafts(draftsFrom(stages));
  }
  const replace = useReplaceRecipeStages(recipeId);

  function update(key: number, patch: Partial<DraftStage>) {
    setDrafts((list) => list.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }

  function move(index: number, by: -1 | 1) {
    setDrafts((list) => {
      const next = [...list];
      const target = index + by;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target]!, next[index]!];
      return normalizeRouting(next);
    });
  }

  /** A route to a stage that is gone, or no longer later, goes back to the next stage. */
  function remove(key: number) {
    setDrafts((list) => normalizeRouting(list.filter((d) => d.key !== key)));
  }

  function setIncludeInNext(index: number, include: boolean) {
    setDrafts((list) =>
      list.map((d, i) =>
        i !== index ? d : { ...d, includeInNext: include, flowsIntoKey: include ? null : defaultHeldTarget(list, index) },
      ),
    );
  }

  function toggleStep(key: number, number: number) {
    setDrafts((list) =>
      list.map((d) =>
        d.key !== key
          ? d
          : {
              ...d,
              stepNumbers: d.stepNumbers.includes(number)
                ? d.stepNumbers.filter((n) => n !== number)
                : [...d.stepNumbers, number].sort((a, b) => a - b),
            },
      ),
    );
  }

  /** A line joins at one stage, so taking it here takes it from wherever it was. */
  function toggleLine(key: number, lineId: number) {
    setDrafts((list) =>
      list.map((d) => {
        if (d.key === key) {
          return {
            ...d,
            ingredientIds: d.ingredientIds.includes(lineId)
              ? d.ingredientIds.filter((id) => id !== lineId)
              : [...d.ingredientIds, lineId],
          };
        }
        return { ...d, ingredientIds: d.ingredientIds.filter((id) => id !== lineId) };
      }),
    );
  }

  function add(kind: RecipeStageInput["kind"]) {
    setDrafts((list) => [
      ...list,
      { key: nextKey++, kind, action: "", detail: "", stepNumbers: [], ingredientIds: [], includeInNext: true, flowsIntoKey: null },
    ]);
  }

  const missingName = drafts.some((d) => d.action.trim() === "");

  async function save() {
    try {
      const routing = routingInputs(drafts);
      await replace.mutateAsync(
        drafts.map(({ kind, action, detail, stepNumbers, ingredientIds }, index) => ({
          kind,
          action: action.trim(),
          detail: detail?.trim() || null,
          stepNumbers: kind === "TASK" ? [] : stepNumbers,
          ingredientIds: kind === "TASK" ? [] : ingredientIds,
          ...routing[index]!,
        })),
      );
      toast.success(t`Stages saved`);
      onOpenChange(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to save the stages`));
    }
  }

  const unassigned = unassignedLines(drafts, lines);
  const summary = flowSummary(drafts);
  /** "Stage 4" or "Stage 4 · Chop", by the stage's place among the stages. */
  const stageLabel = (key: number) => {
    const stages = drafts.filter((d) => d.kind === "STAGE");
    const place = stages.findIndex((d) => d.key === key) + 1;
    const name = stages[place - 1]?.action.trim();
    return name ? t`Stage ${place} · ${name}` : t`Stage ${place}`;
  };
  const summaryText = summary
    .map((entry) => {
      if (entry.held) return t`Stage ${entry.from} → Stage ${entry.to} (held)`;
      return entry.to === null ? t`Stage ${entry.from} → Done` : t`Stage ${entry.from} → Stage ${entry.to}`;
    })
    .join(", ");

  const stageNameOf = (lineId: number, exceptKey: number) =>
    drafts.find((d) => d.key !== exceptKey && d.ingredientIds.includes(lineId))?.action;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      size="full"
      title={t`Edit stages`}
      description={t`Group the method into stages: what each does, the steps it covers, and the ingredients that go in at it.`}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => add("TASK")} className="gap-1.5">
              <Plus className="size-4" />
              {t`Add task`}
            </Button>
            <Button type="button" variant="outline" onClick={() => add("STAGE")} className="gap-1.5">
              <Plus className="size-4" />
              {t`Add stage`}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t`Cancel`}
            </Button>
            <Button type="button" onClick={save} disabled={replace.isPending || missingName}>
              {replace.isPending ? t`Saving…` : t`Save stages`}
            </Button>
          </div>
        </div>
      }
    >
      {summary.length > 0 && (
        <p className="text-sm text-(--color-text-secondary)">
          <span className="font-semibold text-(--color-text-primary)">{t`Flow`}: </span>
          <span>{summaryText}</span>
        </p>
      )}
      {unassigned.length > 0 && (
        <div
          role="status"
          className="flex flex-col gap-1.5 rounded-xl border border-(--color-warning) bg-(--color-warning-bg) p-3 text-sm text-(--color-warning-text)"
        >
          <span className="flex items-center gap-1.5 font-semibold">
            <TriangleAlert className="size-4" />
            {t`Ingredients not in any stage`}
          </span>
          <p>{t`These ingredients are not assigned to a stage. You can still save, and they will be listed as not staged yet.`}</p>
          <ul aria-label={t`Ingredients not in any stage`} className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
            {unassigned.map((line) => (
              <li key={line.id} className="rounded-md border border-(--color-warning) px-2 py-0.5">
                {ingredientLineName(line)}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1">
        {drafts.length === 0 && (
          <p className="text-sm text-(--color-text-secondary)">
            {t`No stages yet. Add the first stage, like "Sweat" or "Mix", or a task like "Preheat oven".`}
          </p>
        )}
        {drafts.map((draft, index) => (
          <div key={draft.key} className="flex flex-col gap-3 rounded-xl border border-(--color-border) p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-(--color-surface-muted) px-2.5 py-0.5 text-xs font-semibold text-(--color-text-secondary)">
                {draft.kind === "TASK" ? t`Task` : t`Stage`}
              </span>
              <Input
                value={draft.action}
                onChange={(e) => update(draft.key, { action: e.target.value })}
                placeholder={draft.kind === "TASK" ? t`Preheat oven to 350°F` : t`Sweat`}
                aria-label={draft.kind === "TASK" ? t`Task name` : t`Stage name`}
                className="h-9 w-48"
                maxLength={80}
              />
              <Input
                value={draft.detail ?? ""}
                onChange={(e) => update(draft.key, { detail: e.target.value })}
                placeholder={t`med heat · chill`}
                aria-label={draft.kind === "TASK" ? t`Task detail` : t`Stage detail`}
                className="h-9 min-w-0 flex-1"
                maxLength={200}
              />
              <div className="flex gap-1">
                <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={t`Move up`} onClick={() => move(index, -1)} disabled={index === 0}>
                  <ArrowUp className="size-4" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="size-8" aria-label={t`Move down`} onClick={() => move(index, 1)} disabled={index === drafts.length - 1}>
                  <ArrowDown className="size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 text-(--color-danger-text)"
                  aria-label={t`Remove`}
                  onClick={() => remove(draft.key)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>

            {draft.kind === "STAGE" && steps.length > 0 && (
              <ChipRow label={t`Steps`}>
                {steps.map((step) => (
                  <Chip
                    key={step.number}
                    selected={draft.stepNumbers.includes(step.number)}
                    onClick={() => toggleStep(draft.key, step.number)}
                    title={step.text}
                  >
                    {step.number}
                  </Chip>
                ))}
              </ChipRow>
            )}

            {draft.kind === "STAGE" && lines.length > 0 && (
              <ChipRow label={t`Ingredients that go in here`}>
                {lines.map((line) => {
                  const elsewhere = stageNameOf(line.id, draft.key);
                  return (
                    <Chip
                      key={line.id}
                      selected={draft.ingredientIds.includes(line.id)}
                      muted={!!elsewhere}
                      onClick={() => toggleLine(draft.key, line.id)}
                      title={elsewhere ? t`In ${elsewhere}` : undefined}
                    >
                      {ingredientLineName(line)}
                    </Chip>
                  );
                })}
              </ChipRow>
            )}

            {draft.kind === "STAGE" && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-semibold text-(--color-text-secondary)">{t`When this stage is done`}</span>
                {laterStages(drafts, index).length === 0 ? (
                  <span className="text-sm text-(--color-text-primary)">{t`Outcome is the finished batch`}</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`include-next-${draft.key}`}
                        checked={draft.includeInNext}
                        onCheckedChange={(checked) => setIncludeInNext(index, checked)}
                      />
                      <Label htmlFor={`include-next-${draft.key}`} className="text-sm text-(--color-text-primary)">
                        {t`Include in next stage`}
                      </Label>
                    </div>
                    {!draft.includeInNext && (
                      <Select
                        value={draft.flowsIntoKey === null ? undefined : String(draft.flowsIntoKey)}
                        onValueChange={(value) => update(draft.key, { flowsIntoKey: Number(value) })}
                      >
                        <SelectTrigger aria-label={t`Flows into`} className="h-9 w-56">
                          <SelectValue placeholder={t`Choose a stage`} />
                        </SelectTrigger>
                        <SelectContent>
                          {laterStages(drafts, index).map((later) => (
                            <SelectItem key={later.key} value={String(later.key)}>
                              {stageLabel(later.key)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </AppDialog>
  );
}

function ChipRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-(--color-text-secondary)">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({
  selected,
  muted = false,
  onClick,
  title,
  children,
}: {
  selected: boolean;
  muted?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={title}
      onClick={onClick}
      className={cn(
        "rounded-lg border px-2.5 py-1 text-sm transition-colors",
        selected
          ? "border-(--color-surface-sidebar) bg-(--color-surface-sidebar) text-(--color-text-on-dark)"
          : muted
            ? "border-dashed border-(--color-border) text-(--color-text-secondary)"
            : "border-(--color-border) text-(--color-text-primary) hover:bg-(--color-surface-muted)",
      )}
    >
      {children}
    </button>
  );
}
